// covered-by: qa/rickrubin/tests/specs/cca/ask.puntt.campaign.spec.ts
import { ArrowUp, CircleNotch } from "@phosphor-icons/react";
import { Button, Flex, Heading, Text, TextArea } from "@radix-ui/themes";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { askPunttChatRoute } from "./chatRoute";
import { AskPunttDropZone } from "./components/AskPunttDropZone";
import { HomeTaskList } from "./components/HomeTaskList";
import { InputPlusMenu } from "./components/InputPlusMenu";
import { type ConnectorOption } from "./components/InputPlusMenu";
import { PasswordProtectedPdfDialog } from "./components/PasswordProtectedPdfDialog";
import {
  ReviewModeControl,
  type ReviewMode,
} from "./components/ReviewModeControl";
import { SkillCards } from "./components/WorkflowCards";
import { CONNECTOR_ICONS } from "./constants";
import { useAttachedFiles } from "./hooks/useAttachedFiles";
import { useFileDrop } from "./hooks/useFileDrop";
import { useUiPrefs } from "./hooks/useUiPrefs";
import { WORKFLOW_CARDS } from "./mockData";
import { resolveNewProjectWorkflowType } from "./projectWorkflowType";

import { getGuidelines } from "../../services/brand-guidelines";
import { PasswordProtectedPdfError } from "../../services/upload";
import { errorAnalyticsPayload, useAnalytics } from "../../utils/analytics";
import { canUseAskPunttConnectors } from "../../utils/auth";
import { useCreateAskPunttChat } from "../../utils/queries/askPuntt";
import { useParticipationTasks } from "../../utils/queries/askPunttProjects";

/** Matches the centered composer column — workflows share this width. */
const ASK_PUNTT_HOME_CONTENT_CLASS = "mx-auto w-full max-w-[900px] px-4";

export default function AskPunttHome() {
  const [inputMessage, setInputMessage] = useState("");
  const navigate = useNavigate();
  const createChat = useCreateAskPunttChat();
  const analytics = useAnalytics("ask_puntt_home");
  const {
    attachedItems,
    addFiles,
    addFile,
    removeFile,
    clearAll,
    readyAttachments,
    hasPending,
  } = useAttachedFiles({
    onUploadError: (err) => {
      if (err instanceof PasswordProtectedPdfError) {
        setProtectedPdfFileName(err.fileName);
      }
      analytics.capture("ask_puntt_upload_error", {
        ...errorAnalyticsPayload(err),
      });
    },
  });
  const [protectedPdfFileName, setProtectedPdfFileName] = useState<
    string | null
  >(null);
  // Page-wide drag-and-drop is owned by the outer AskPunttDropZone wrapper
  // below. Only retain the clipboard paste handler here so users can still
  // paste screenshots / images directly into the composer.
  const { handlePaste } = useFileDrop({ onDrop: addFiles });
  const guidelinesQuery = useQuery({
    queryKey: ["brand-guidelines"],
    queryFn: getGuidelines,
  });
  const enabledSkillIds = guidelinesQuery.data?.askPunttSkills
    ?.filter((s) => s.enabled)
    .map((s) => s.skillId);
  const customWorkflows = guidelinesQuery.data?.customWorkflows;

  const hasWorkflows =
    (enabledSkillIds
      ? WORKFLOW_CARDS.some((c) => enabledSkillIds.includes(c.skillId))
      : WORKFLOW_CARDS.length > 0) ||
    (customWorkflows ?? []).some((wf) => wf.enabled);

  const openTasksQuery = useParticipationTasks({ status: "open" });
  const hasActiveTasks = (openTasksQuery.data?.tasks?.length ?? 0) > 0;

  // Derive available connectors from enterprise settings — show all enabled,
  // mark unconnected ones as disabled (greyed out in UI). Role-gated: OPS
  // doesn't see connectors (`canUseAskPunttConnectors()` returns false),
  // matching `ask.puntt.connectors.spec.ts > Not visible for OPS`.
  const availableConnectors: ConnectorOption[] = useMemo(
    () =>
      canUseAskPunttConnectors()
        ? (guidelinesQuery.data?.askPunttConnectors ?? [])
            .filter((c) => c.enabled)
            .map((c) => ({
              id: c.connectorId,
              label: `${c.name} search`,
              icon: CONNECTOR_ICONS[c.connectorId] ?? "🔗",
              disabled: !c.connected,
            }))
        : [],
    [guidelinesQuery.data?.askPunttConnectors],
  );

  const [enabledConnectors, setEnabledConnectors] = useState<
    Record<string, boolean>
  >({});

  // Standard/Deep review-engine selector, owned by the composer dropdown. The
  // choice is persisted in UI prefs and rides on the create-chat payload as
  // `reviewMode`, which drives v1/v2 routing server-side.
  const { prefs, setPref } = useUiPrefs();
  const reviewMode: ReviewMode = prefs.artworkReviewMode ?? "standard";
  const setReviewMode = (mode: ReviewMode) =>
    setPref("artworkReviewMode", mode);

  // Initialize connector state when enterprise data loads
  useEffect(() => {
    if (availableConnectors.length > 0) {
      setEnabledConnectors((prev) => {
        if (Object.keys(prev).length > 0) return prev;
        const initial: Record<string, boolean> = {};
        availableConnectors.forEach((c) => {
          initial[c.id] = !c.disabled;
        });
        return initial;
      });
    }
  }, [availableConnectors]);

  useEffect(() => {
    analytics.capture("ask_puntt_home_viewed");

    // Auto-focus the textarea when the page loads
    const timer = setTimeout(() => {
      // Radix UI's TextArea renders as a textarea element itself, not a wrapper
      const textarea = document.querySelector(
        '[data-testid="ask-puntt-home-input"]',
      ) as HTMLTextAreaElement | null;
      if (textarea) {
        textarea.focus();
      }
    }, 150);

    return () => clearTimeout(timer);
  }, []);

  const handleSubmit = async (
    message?: string,
    workflowType?: string,
    opts?: { workflowGreeting?: boolean },
  ) => {
    const msg = message || inputMessage;
    const attachments = [...readyAttachments];
    if (!msg.trim() && attachments.length === 0) return;
    if (hasPending) return;

    const wfType = workflowType || "general";
    const enabledConnectorIds = Object.entries(enabledConnectors)
      .filter(([_, enabled]) => enabled)
      .map(([id]) => id);

    // Only request the canned greeting when nothing was attached — with an
    // attachment the user wants the real review to run (the backend enforces
    // the same guard).
    const workflowGreeting =
      opts?.workflowGreeting === true && attachments.length === 0;

    try {
      const result = await createChat.mutateAsync({
        message: msg,
        workflowType: wfType,
        attachments: attachments.length > 0 ? attachments : undefined,
        enabledConnectors: enabledConnectorIds,
        workflowGreeting: workflowGreeting || undefined,
        // Always send the engine choice: the backend routes to v2 only on an
        // explicit "deep", so an omitted value would mean v1 anyway — sending it
        // keeps the request self-describing and the routing logs honest.
        reviewMode,
      });
      const attachmentIds = attachments.map((att) => att.key);

      analytics.capture("ask_puntt_chat_created", {
        workflow_type: wfType,
        message_length: msg.length,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: enabledConnectorIds,
        workflow_greeting: workflowGreeting,
      });
      clearAll();
      navigate({
        to: askPunttChatRoute.to,
        params: { chatId: result.chat._id },
      });
    } catch (error) {
      const attachmentIds = attachments.map((att) => att.key);

      analytics.capture("ask_puntt_chat_create_error", {
        workflow_type: wfType,
        has_attachments: attachments.length > 0,
        attachment_count: attachments.length,
        attachment_ids: attachmentIds,
        enabled_connectors: enabledConnectorIds,
        ...errorAnalyticsPayload(error),
      });
      console.error("Failed to create chat:", error);
    }
  };

  const handleWorkflowClick = (cardId: string) => {
    const cardMessages: Record<string, string> = {
      marketing_review: "I need a marketing review",
      artwork_review: "I need an artwork review",
      claims_intelligence: "I want to check a claim",
      shared_projects: "I want to set up a shared project",
      connectors: "Show me connectors",
    };
    const card = WORKFLOW_CARDS.find((c) => c.id === cardId);

    // The Shared Projects card resolves its workflowType via
    // resolveNewProjectWorkflowType (Vitalora demo → "general", so the
    // project_management overlay doesn't fight the live-AI baseline-capture
    // overlay — see projectWorkflowType.ts). Other cards open their declared
    // workflowType.
    const workflowType =
      cardId === "shared_projects"
        ? resolveNewProjectWorkflowType()
        : (card?.workflowType ?? cardId);

    // Only home page workflow cards open with the canned greeting.
    const workflowGreeting = card != null;

    let label = cardMessages[cardId];
    if (!label && cardId.startsWith("custom_")) {
      const wfId = cardId.replace("custom_", "");
      const wf = customWorkflows?.find((w) => w.workflowId === wfId);
      label = wf ? `I need help with ${wf.name}` : "Hello";
    }

    analytics.capture("ask_puntt_workflow_card_clicked", {
      workflow_type: workflowType,
      card_id: cardId,
      card_title: label ?? cardId,
    });
    handleSubmit(label || "Hello", workflowType, { workflowGreeting });
  };

  return (
    <AskPunttDropZone
      onFilesDropped={addFiles}
      className="ask-puntt-mesh-bg ask-puntt-noise relative flex h-full flex-col"
      data-testid="ask-puntt-home"
    >
      {/* Centered heading + input */}
      <div
        className={`${ASK_PUNTT_HOME_CONTENT_CLASS} flex flex-1 flex-col ${
          hasActiveTasks ? "justify-start pt-16" : "justify-center"
        }`}
      >
        <div className="stagger-children mb-8 flex flex-col items-center gap-3">
          <div className="animate-glow-ring relative z-10 flex size-16 items-center justify-center rounded-2xl bg-puntt-accent-3 shadow-lg">
            <svg
              width="36"
              height="35"
              viewBox="0 0 30 28"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M22.4939 5.67486H15.8328V2.34435C15.8328 2.12352 15.7451 1.91174 15.589 1.75559C15.4328 1.59944 15.221 1.51172 15.0002 1.51172C14.7794 1.51172 14.5676 1.59944 14.4114 1.75559C14.2553 1.91174 14.1676 2.12352 14.1676 2.34435V5.67486H7.50654C6.62323 5.67486 5.7761 6.02576 5.15151 6.65035C4.52692 7.27494 4.17603 8.12207 4.17603 9.00538V20.6622C4.17603 21.5455 4.52692 22.3926 5.15151 23.0172C5.7761 23.6418 6.62323 23.9927 7.50654 23.9927H22.4939C23.3772 23.9927 24.2243 23.6418 24.8489 23.0172C25.4735 22.3926 25.8244 21.5455 25.8244 20.6622V9.00538C25.8244 8.12207 25.4735 7.27494 24.8489 6.65035C24.2243 6.02576 23.3772 5.67486 22.4939 5.67486Z"
                fill="rgb(var(--puntt-blue-9))"
              />
              <circle
                cx="10.428"
                cy="13.5947"
                r="2.91667"
                stroke="white"
                strokeWidth="1.66667"
              />
              <circle
                cx="19.5947"
                cy="13.5947"
                r="2.91667"
                stroke="white"
                strokeWidth="1.66667"
              />
            </svg>
          </div>
          <Text
            size="8"
            weight="bold"
            className="text-center text-puntt-neutral-gray-12"
          >
            Ask{" "}
            <span
              style={{
                backgroundImage:
                  "linear-gradient(135deg, rgb(var(--puntt-blue-9)), rgb(var(--puntt-blue-11)))",
                WebkitBackgroundClip: "text",
                color: "transparent",
              }}
            >
              Puntt
            </span>
          </Text>
        </div>

        <div
          className="relative z-10 overflow-hidden rounded-2xl border border-puntt-neutral-gray-6 bg-base-white shadow-lg transition-all duration-200 focus-within:border-puntt-accent-7"
          data-testid="ask-puntt-home-composer"
        >
          <TextArea
            data-testid="ask-puntt-home-input"
            size="3"
            variant="surface"
            placeholder="Ask Puntt anything..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
            onPaste={handlePaste}
            className="w-full resize-none"
            style={{ height: "88px" }}
          />

          {/* Attached files preview */}
          {attachedItems.length > 0 && (
            <Flex gap="2" wrap="wrap" className="px-3 pb-1">
              {attachedItems.map((item, idx) => (
                <Flex
                  key={item.id}
                  align="center"
                  gap="1"
                  className="rounded-md bg-puntt-neutral-gray-3 px-2 py-1"
                  data-testid="ask-puntt-attached-file"
                >
                  {item.status === "pending" && (
                    <CircleNotch
                      size={12}
                      weight="bold"
                      className="shrink-0 animate-spin text-puntt-neutral-gray-9"
                    />
                  )}
                  <Text
                    size="1"
                    className="max-w-[150px] truncate text-puntt-neutral-gray-11"
                  >
                    {item.file.name}
                    {item.status === "error" && " (failed)"}
                  </Text>
                  <button
                    data-testid="home-button-302"
                    onClick={() => removeFile(idx)}
                    className="text-puntt-neutral-gray-9 hover:text-puntt-neutral-gray-12"
                    aria-label={`Remove ${item.file.name}`}
                  >
                    ×
                  </button>
                </Flex>
              ))}
            </Flex>
          )}

          <Flex justify="between" align="end" className="p-1">
            <InputPlusMenu
              iconSize={16}
              onFilesSelected={addFiles}
              onScreenshotCaptured={addFile}
              enabledConnectors={enabledConnectors}
              onConnectorToggle={(id, enabled) => {
                setEnabledConnectors((prev) => ({ ...prev, [id]: enabled }));
              }}
              availableConnectors={availableConnectors}
            />

            <Flex align="center" gap="2">
              <ReviewModeControl
                mode={reviewMode}
                onModeChange={setReviewMode}
              />
              <Button
                data-testid="ask-puntt-home-submit"
                size="2"
                className="cursor-pointer rounded-xl"
                disabled={
                  (!inputMessage.trim() && readyAttachments.length === 0) ||
                  createChat.isPending ||
                  hasPending
                }
                onClick={() => handleSubmit()}
              >
                {hasPending ? (
                  <CircleNotch
                    size={14}
                    weight="bold"
                    className="animate-spin"
                  />
                ) : (
                  <ArrowUp size={14} weight="bold" />
                )}
                Ask Puntt
              </Button>
            </Flex>
          </Flex>
        </div>

        <HomeTaskList />
      </div>

      {/* Workflows anchored to bottom */}
      <div
        className={`${ASK_PUNTT_HOME_CONTENT_CLASS} relative z-10 shrink-0 pb-6`}
        data-testid="ask-puntt-home-bottom"
      >
        {hasWorkflows && (
          <>
            <Flex justify="between" align="center" className="mb-3">
              <Heading
                as="h2"
                size="4"
                weight="bold"
                className="text-puntt-neutral-gray-12"
              >
                Workflows
              </Heading>
            </Flex>
            <SkillCards
              onCardClick={handleWorkflowClick}
              enabledSkillIds={enabledSkillIds}
              customWorkflows={customWorkflows}
            />
          </>
        )}
      </div>
      <PasswordProtectedPdfDialog
        fileName={protectedPdfFileName}
        onClose={() => setProtectedPdfFileName(null)}
      />
    </AskPunttDropZone>
  );
}
